import { z } from 'zod';

export const traceSchema = z.object({
    idNo: z.string().length(13, "ID Number must be 13 digits").regex(/^\d+$/, "ID Number must be numeric").optional().or(z.literal('')),
    passportNo: z.string().regex(/^[a-zA-Z0-9]+$/, "Passport Number must be alphanumeric").optional().or(z.literal('')),
    firstName: z.string().optional().or(z.literal('')),
    surname: z.string().optional().or(z.literal('')),
    dob: z.string().refine((val) => !val || new Date(val) <= new Date(), "Date of Birth cannot be in the future").optional().or(z.literal('')),
    voucherCode: z.string().optional().or(z.literal('')),
    yourReference: z.string().optional().or(z.literal('')),
}).superRefine((data, ctx) => {
    const idNo = data.idNo === '' ? undefined : data.idNo;
    const passportNo = data.passportNo === '' ? undefined : data.passportNo;
    const firstName = data.firstName === '' ? undefined : data.firstName;
    const surname = data.surname === '' ? undefined : data.surname;
    const dob = data.dob === '' ? undefined : data.dob;

    const hasIDOrPassport = !!idNo || !!passportNo;
    const hasFullDetails = !!firstName && !!surname && !!dob;

    if (!hasIDOrPassport && !hasFullDetails) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Please provide either an ID/Passport Number OR First Name, Surname, and Date of Birth",
            path: ['idNo'],
        });
    }

    if (!hasIDOrPassport && (firstName || surname || dob)) {
        if (!firstName) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "First Name is required", path: ['firstName'] });
        if (!surname) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Surname is required", path: ['surname'] });
        if (!dob) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Date of Birth is required", path: ['dob'] });
    }
});

export type ConsumerTracePayload = z.infer<typeof traceSchema>;

// ── Data contract types ────────────────────────────────────────────────────

export interface ConsumerMatchItem {
    consumer_id: string | null;
    first_name: string | null;
    surname: string | null;
    id_number: string | null;
    birth_date: string | null;
    gender: string | null;
    enquiry_id: string;
    enquiry_result_id: string;
    reference: string | null;
}

export interface ConsumerMatchResponse {
    success: boolean;
    message: string;
    log_id: string;
    matches: ConsumerMatchItem[];
}

export interface ConsumerResultPayload {
    enquiryId: number;
    enquiryResultId: number;
    yourReference?: string;
    voucherCode?: string;
}

export interface ConsumerTraceResponse {
    message: string;
    success: boolean;
    log_id: string;
    report_data: any;
}

// ── API calls ──────────────────────────────────────────────────────────────

const BASE_URL = '/verification/consumer-trace';

export const consumerTraceService = {
    createPayload: (formData: z.infer<typeof traceSchema>): ConsumerTracePayload => ({
        ...formData,
    }),

    getMatch: async (payload: ConsumerTracePayload): Promise<ConsumerMatchResponse> => {
        const axios = (await import('../axiosConfig')).default;
        const response = await axios.post<ConsumerMatchResponse>(`${BASE_URL}/match`, payload);
        return response.data;
    },

    getResult: async (payload: ConsumerResultPayload): Promise<ConsumerTraceResponse> => {
        const axios = (await import('../axiosConfig')).default;
        const response = await axios.post<ConsumerTraceResponse>(`${BASE_URL}/result`, payload);
        return response.data;
    },
};
